import React, { FC, useMemo } from 'react'
import { useHistory } from 'react-router'
import classNames from 'classnames'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { Box, createStyles, makeStyles, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useBookableShipmentSavedSearchContext } from 'apps/fleet-planner/context/BookableShipmentSavedSearchContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics/useFMSAnalytics'
import useTruckAvailability from 'apps/fleet-planner/hooks/useTruckAvailability'
import { TrucksIcon } from 'transfix-icons'
import { BookableShipmentSavedSearchTile } from './BookableShipmentSavedSearchTile'

interface IBookableShipmentSavedSearchTruckTileProps {
  isLoading: boolean
}

const useStyles = makeStyles(theme => {
  return createStyles({
    labelTwo: {
      color: brandColors.coolGray6,
    },
    icon: {
      marginLeft: '10px',
      marginTop: '15px',
    },
    iconContainer: {
      height: '85px',
      width: '85px',
      marginBottom: theme.spacing(1),
    },
    tileContainer: {
      width: '85px',
      textAlign: 'center',
      margin: 'auto',
    },
  })
})

export const BookableShipmentSavedSearchTruckTile: FC<IBookableShipmentSavedSearchTruckTileProps> = ({
  isLoading,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()
  const { savedSearches = [] } = useBookableShipmentSavedSearchContext()
  const { availableTruckDays = [] } = useTruckAvailability({
    savedSearches,
    initialTotalBaseDays: 1,
  })
  const todaysPostedTrucksCount = availableTruckDays[0]?.totalTrucksPosted || 0
  const history = useHistory()
  const classes = useStyles()

  const truckSavedSearches = useMemo(() => {
    return savedSearches.filter(s => s.sourceType === 'dispatcher_truck_availability')
  }, [savedSearches])

  const truckSavedSearchesTotalResultCount = useMemo(
    () =>
      truckSavedSearches.reduce(
        (sum, savedSearch) => sum + (savedSearch?.totalResultCount || 0),
        0
      ),
    [truckSavedSearches]
  )

  const postedTrucksStr = useMemo(() => {
    return todaysPostedTrucksCount === 1
      ? `${todaysPostedTrucksCount} truck posted today`
      : `${todaysPostedTrucksCount} trucks posted today`
  }, [todaysPostedTrucksCount])

  const reloadStr = useMemo(() => {
    return truckSavedSearchesTotalResultCount === 1
      ? `${truckSavedSearchesTotalResultCount} reload`
      : `${truckSavedSearchesTotalResultCount} reloads`
  }, [truckSavedSearchesTotalResultCount])

  const clickHandler = () => {
    trackEvent('Book Load', 'CLICKED_POST_MY_TRUCKS')
    history.push('/truck-availability')
  }

  const reloads = !isMobile ? reloadStr : ''
  const tileContainer = classNames({
    [classes.tileContainer]: isMobile,
  })

  return (
    <BookableShipmentSavedSearchTile
      dataTest='transfix-lanes-truck-tile'
      isLoading={isLoading}
      onClick={clickHandler}
      countStr={todaysPostedTrucksCount ? reloads : ''}
      variant='mobile'
      header={
        <Box className={tileContainer}>
          {!isMobile && (
            <>
              <Typography variant='subtitle1'>Post My Trucks</Typography>
              {isLoading ? (
                <Skeleton width='100%' />
              ) : (
                <Typography className={classes.labelTwo} variant='body2'>
                  {postedTrucksStr}
                </Typography>
              )}
            </>
          )}
          {isMobile && (
            <>
              <Tag
                label={<TrucksIcon size='xxlarge' className={classes.icon} />}
                tagStyle={TAG_STYLE.info}
                size={TAG_SIZE.large}
                className={classes.iconContainer}
              />
              <Typography variant='subtitle1'>Post My Trucks</Typography>
            </>
          )}
        </Box>
      }
    />
  )
}
